import { useState, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Modal,
  Alert,
  Platform,
  RefreshControl,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, TRACKING, SPACE, CARD, BUTTON, SECTION_LABEL } from '@/constants/designTokens';
import { Swords, X , ChevronDown, ChevronUp, Info } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import EmptyState from './EmptyState';
import SkeletonRow from './SkeletonRow';
import CreateDuelWizard from './CreateDuelWizard';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { hapticsUtil } from '@/lib/haptics';
import { fetchBaselineStatus } from '@/lib/baselineClient';
import type { BaselineStatus } from '@/lib/baselineGate';
import BaselineGateOverlay from '@/components/BaselineGateOverlay';
import { checkDuelCooldown } from '@/lib/matchmaking';
import { captureError } from '@/lib/errorReporting';
import { notifyDuelAccepted } from '@/lib/notificationTriggers';
import { resolveExpiredDuels } from '@/lib/duelResolution';
import { DuelCard, DuelHistoryFilters, EnhancedDuelCard, DuelResultBanner, DuelDetailModal, DuelAnalytics, DuelInsights } from './duels';
import type { Duel, TimeframeFilter, StatusFilter } from './duels';

import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';
import InfoSheet from '@/components/InfoSheet';
import { INFO_CONTENT } from '@/lib/infoContent';
import { useUserStageLight } from '@/hooks/useUserStage';

const log = createLogger('DUELS');

type DuelTab = 'active' | 'pending' | 'completed';

export default function DuelsScreen() {
  const { profile } = useAuth();
  const { theme } = useTheme();
  const router = useRouter();
  const { tier } = useSubscription();
  const { start_duel } = useLocalSearchParams();
  const [activeTab, setActiveTab] = useState<DuelTab>('active');
  const [duels, setDuels] = useState<Duel[]>([]);
  const [profileById, setProfileById] = useState<Record<string, { display_name?: string | null; handle?: string | null }>>({});
  const [incomingInvites, setIncomingInvites] = useState<Duel[]>([]);
  const [outgoingPending, setOutgoingPending] = useState<Duel[]>([]);
  const [activeDuelsState, setActiveDuelsState] = useState<Duel[]>([]);
  const [completedDuelsState, setCompletedDuelsState] = useState<Duel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [celebratedDuels, setCelebratedDuels] = useState<Set<string>>(new Set());
  const [baselineStatus, setBaselineStatus] = useState<BaselineStatus | null>(null);
  const [showBaselineGate, setShowBaselineGate] = useState(false);
  const [selectedDuel, setSelectedDuel] = useState<Duel | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [recentlyCompletedDuel, setRecentlyCompletedDuel] = useState<Duel | null>(null);
  const [showDuelExplainer, setShowDuelExplainer] = useState(false);
  const [completedDuelsCount, setCompletedDuelsCount] = useState(0);

  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showDuelInfo, setShowDuelInfo] = useState(false);

  const { stage } = useUserStageLight(profile, {
    hasWorkouts: profile?.onboarding_completed === true || (profile?.duel_standard_strength_kg ?? 0) > 0,
    duelCount: completedDuelsCount,
  });

  // NSQ-10: Duel history filters (client-side)
  const [timeframeFilter, setTimeframeFilter] = useState<TimeframeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const filteredCompleted = completedDuelsState.filter((d) => {
    if (statusFilter !== 'all' && d.status !== statusFilter) return false;
    if (timeframeFilter !== 'all') {
      const days = timeframeFilter === '7d' ? 7 : timeframeFilter === '30d' ? 30 : 90;
      const cutoff = Date.now() - days * 86400000;
      if (new Date(d.created_at).getTime() < cutoff) return false;
    }
    return true;
  });

  useEffect(() => {
    if (profile?.id) {
      fetchBaselineStatus(profile.id).then(setBaselineStatus).catch(() => {});
    }
  }, [profile?.id]);

  // Check if user needs to see the duel explainer
  useEffect(() => {
    const checkDuelExplainer = async () => {
      if (!profile?.id) return;
      
      try {
        const dismissed = await AsyncStorage.getItem('duel_explainer_dismissed');
        if (dismissed) {
          setShowDuelExplainer(false);
          return;
        }

        // Count completed duels for this user
        const { data: completedDuels, error } = await supabase
          .from('duels')
          .select('id')
          .or(`challenger_id.eq.${profile.id},challenged_id.eq.${profile.id}`)
          .eq('status', 'completed');

        if (error) {
          log.error('Error counting completed duels:', error);
          return;
        }

        const count = completedDuels?.length || 0;
        setCompletedDuelsCount(count);
        setShowDuelExplainer(count === 0);
      } catch (error) {
        log.error('Error checking duel explainer:', error);
      }
    };

    checkDuelExplainer();
  }, [profile?.id]);

  useEffect(() => {
    autoResolveExpiredDuels();
    loadDuels();
  }, [activeTab]);

  useEffect(() => {
    if (start_duel === '1' && !showCreateModal) {
      if (!profile?.handle?.trim()) {
        // Profile incomplete — don't open create modal
      } else if (baselineStatus && !baselineStatus.canInitiateDuel) {
        setShowBaselineGate(true);
      } else {
        setShowCreateModal(true);
      }
      router.replace('/(tabs)/duels');
    }
  }, [start_duel, router, showCreateModal, baselineStatus]);

  useEffect(() => {
    if (activeTab === 'completed' && duels.length > 0 && profile) {
      const newWonDuels = duels.filter(
        (duel) =>
          duel.status === 'completed' &&
          duel.winner_user_id === profile.id &&
          !celebratedDuels.has(duel.id)
      );

      if (newWonDuels.length > 0) {
        hapticsUtil.duelWin();
        setCelebratedDuels((prev) => {
          const updated = new Set(prev);
          newWonDuels.forEach((duel) => updated.add(duel.id));
          return updated;
        });
      }
    }
  }, [duels, activeTab, profile]);

  // Real-time subscription for duel updates
  useEffect(() => {
    if (!profile?.id) return;
    const channel = supabase
      .channel('duels-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duels',
          filter: `challenger_id=eq.${profile.id}`,
        },
        () => loadDuels()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duels',
          filter: `challenged_id=eq.${profile.id}`,
        },
        () => loadDuels()
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [profile?.id]);

  // Detect recently completed duels (within 5 minutes)
  useEffect(() => {
    if (!profile?.id) return;
    const recent = duels.find((d) => {
      if (d.status !== 'completed' || !d.end_at) return false;
      const endedAgo = Date.now() - new Date(d.end_at).getTime();
      return endedAgo >= 0 && endedAgo < 5 * 60 * 1000;
    });
    setRecentlyCompletedDuel(recent ?? null);
  }, [duels, profile?.id]);

  const autoResolveExpiredDuels = async () => {
    if (!profile?.id) return;
    try {
      const count = await resolveExpiredDuels(profile.id);
      if (count > 0) log.info(`Auto-resolved ${count} expired duels`);
    } catch (err) {
      log.warn('Auto-resolve expired duels failed:', err);
    }
  };

  const loadDuels = async () => {
    if (!profile) return;

    log.info('Loading duels for user:', profile.id, 'tab:', activeTab);

    try {
      let query = supabase
        .from('duels')
        .select(`
          *,
          challenger:profiles!duels_challenger_id_fkey (*),
          opponent:profiles!duels_opponent_id_fkey (*)
        `)
        .or(`challenger_id.eq.${profile.id},challenged_id.eq.${profile.id}`)
        .order('created_at', { ascending: false });

      if (activeTab === 'active') {
        query = query.eq('status', 'active');
      } else if (activeTab === 'pending') {
        query = query.eq('status', 'pending');
      } else {
        query = query.in('status', ['completed', 'cancelled', 'declined', 'expired']);
      }

      const { data, error } = await query;

      if (error) {
        log.error('Error loading duels:', error);
        throw error;
      }

      log.info(`Loaded ${data?.length || 0} duels for tab:`, activeTab);
      const duelsData = (data as Duel[]) || [];
      setDuels(duelsData);

      // Partition duels — expired active duels go to completed
      const isStale = (d: Duel) => d.created_at && (Date.now() - new Date(d.created_at).getTime()) > 7 * 24 * 3600000;
      setIncomingInvites(duelsData.filter((d) => d.status === 'pending' && d.challenged_id === profile.id && !isStale(d)));
      setOutgoingPending(duelsData.filter((d) => d.status === 'pending' && d.challenger_id === profile.id && !isStale(d)));

      const isExpiredActive = (d: Duel) =>
        d.status === 'active' && d.end_at && new Date(d.end_at).getTime() <= Date.now();

      setActiveDuelsState(duelsData.filter((d) => d.status === 'active' && !isExpiredActive(d)));
      setCompletedDuelsState([
        ...duelsData.filter((d) => ['completed', 'expired', 'cancelled', 'declined'].includes(d.status)),
        ...duelsData.filter(isExpiredActive),
      ]);

      try {
        const otherIds = duelsData
          .map((d) => (d.challenger_id === profile.id ? d.challenged_id : d.challenger_id))
          .filter((id): id is string => !!id);

        const uniqueOtherIds = Array.from(new Set(otherIds));

        if (uniqueOtherIds.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id,handle,display_name,gym_id,avatar_url')
            .in('id', uniqueOtherIds);

          if (!profilesError && profilesData) {
            const map: Record<string, { display_name?: string | null; handle?: string | null }> = {};
            (profilesData as { id: string; display_name?: string | null; handle?: string | null }[]).forEach((p) => {
              map[p.id] = {
                display_name: p.display_name,
                handle: p.handle,
              };
            });
            setProfileById(map);
          } else {
            setProfileById({});
          }
        } else {
          setProfileById({});
        }
      } catch (err) {
        log.error('Failed to load opponent profiles:', err);
        setProfileById({});
      }
    } catch (error) {
      log.error('Failed to load duels:', error);
      captureError(error, { context: 'duels.loadDuels' });
    } finally {
      setLoading(false);
    }
  };

  const acceptDuel = async (duelId: string) => {
    hapticsUtil.duelCreated();

    const now = new Date();
    const duel = duels.find((d) => d.id === duelId);
    if (!duel) return;

    const endAt = new Date(now.getTime() + duel.timeframe_hours * 60 * 60 * 1000);

    log.info('Accepting duel:', duelId);

    try {
      const { error } = await supabase
        .from('duels')
        .update({
          status: 'active',
          start_at: now.toISOString(),
          end_at: endAt.toISOString(),
        })
        .eq('id', duelId);

      if (error) {
        log.error('Failed to accept duel:', error);
        throw error;
      }

      log.info('Duel accepted successfully');
      hapticsUtil.duelCreated();

      // Notify the challenger via push notification
      const myHandle = profile?.handle || profile?.display_name || 'Someone';
      notifyDuelAccepted(duel.challenger_id, myHandle, duelId).catch(() => {});

      if (Platform.OS === 'web') {
        alert('Duel accepted.');
      } else {
        Alert.alert('Duel Accepted', 'Challenge is now active.');
      }

      loadDuels();
    } catch (error: unknown) {
      log.error('Failed to accept duel:', error);

      if (Platform.OS === 'web') {
        alert('Something went wrong. Please try again.');
      } else {
        Alert.alert('Something went wrong', 'Couldn\'t accept the duel. Please try again.');
      }
    }
  };

  const cancelDuel = async (duelId: string) => {
    log.info('Cancelling duel:', duelId);
    try {
      const { error } = await supabase
        .from('duels')
        .update({ status: 'cancelled' })
        .eq('id', duelId)
        .eq('challenger_id', profile!.id);
      if (error) throw error;
      if (Platform.OS === 'web') {
        alert('Duel cancelled.');
      } else {
        Alert.alert('Duel Cancelled', 'The challenge has been cancelled.');
      }
      loadDuels();
    } catch (error: unknown) {
      log.error('Failed to cancel duel:', error);
      if (Platform.OS === 'web') {
        alert('Something went wrong. Please try again.');
      } else {
        Alert.alert('Something went wrong', 'Couldn\'t cancel the duel. Please try again.');
      }
    }
  };

  const rejectDuel = async (duelId: string) => {
    log.info('Rejecting duel:', duelId);

    try {
      const { error } = await supabase
        .from('duels')
        .update({ status: 'declined' })
        .eq('id', duelId);

      if (error) {
        log.error('Failed to reject duel:', error);
        throw error;
      }

      log.info('Duel declined successfully');

      if (Platform.OS === 'web') {
        alert('Duel Declined');
      } else {
        Alert.alert('Duel Declined', 'The challenge has been declined.');
      }

      loadDuels();
    } catch (error: unknown) {
      log.error('Failed to reject duel:', error);

      if (Platform.OS === 'web') {
        alert('Something went wrong. Please try again.');
      } else {
        Alert.alert('Something went wrong', 'Couldn\'t decline the duel. Please try again.');
      }
    }
  };

  const dismissDuelExplainer = async () => {
    try {
      await AsyncStorage.setItem('duel_explainer_dismissed', 'true');
      setShowDuelExplainer(false);
    } catch (error) {
      log.error('Error dismissing duel explainer:', error);
    }
  };

  // Newcomer: show locked explanatory card
  if (stage === 'newcomer') {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <LinearGradient
          colors={[theme.colors.background, theme.colors.surfaceVariant]}
          style={[styles.header, { borderBottomColor: theme.colors.border }]}>
          <Text style={[styles.headerTitle, { color: theme.colors.text }]}>DUELS</Text>
        </LinearGradient>
        <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: SPACE.lg }}>
          <View style={[styles.newcomerCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>  
            <Swords size={40} color={theme.colors.textTertiary} style={{ alignSelf: 'center', marginBottom: 12 }} />
            <Text style={[styles.newcomerCardTitle, { color: theme.colors.text }]}>HEAD-TO-HEAD LIFTING CHALLENGES</Text>
            <Text style={[styles.newcomerCardDesc, { color: theme.colors.textSecondary }]}>
              Duels are head-to-head lifting challenges. Log your first workout to unlock this feature.
            </Text>
            <TouchableOpacity
              style={[styles.createButton, { backgroundColor: theme.colors.accent, marginTop: 16 }]}
              onPress={() => router.push('/(tabs)/record')}>
              <Text style={[styles.createButtonText, { color: theme.colors.accentForeground }]}>GO TO RECORD</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <LinearGradient
        colors={[theme.colors.background, theme.colors.surfaceVariant]}
        style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        {stage === 'scored' && (
          <Text style={[styles.readyToCompete, { color: theme.colors.accent }]}>READY TO COMPETE?</Text>
        )}
        <TouchableOpacity
          style={[
            styles.createButton,
            {
              backgroundColor: '#B8972E',
              borderWidth: 2,
              borderColor: '#8A7122',
            },
          ]}
          onPress={async () => {
            if (!profile?.handle?.trim()) {
              if (Platform.OS === 'web') {
                if (confirm('Set a username in your profile before creating a duel. Go to Edit Profile?')) {
                  router.push('/(tabs)/edit-profile');
                }
              } else {
                Alert.alert('Profile Incomplete', 'Set a username in your profile before creating a duel.', [
                  { text: 'Edit Profile', onPress: () => router.push('/(tabs)/edit-profile') },
                  { text: 'Cancel', style: 'cancel' },
                ]);
              }
              return;
            }
            if (baselineStatus && !baselineStatus.canInitiateDuel) {
              setShowBaselineGate(true);
              return;
            }
            setShowCreateModal(true);
          }}>
          <Swords size={20} color="#030303" />
          <Text style={[styles.createButtonText, { color: '#030303' }]}>NEW DUEL</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setShowDuelInfo(true)} style={{ padding: 6, opacity: 0.7 }} accessibilityRole="button" accessibilityLabel="What is a duel?">
          <Info size={18} color={theme.colors.textSecondary} />
        </TouchableOpacity>
      </LinearGradient>

      {/* Tab bar */}
      <View style={[styles.tabs, { borderBottomColor: theme.colors.border }]}>
        {((['active', 'pending', 'completed'] as DuelTab[]).filter((tab) =>
          stage === 'scored' ? tab !== 'completed' : true
        )).map((tab) => (
          <TouchableOpacity
            key={tab}
            style={[
              styles.tab,
              activeTab === tab && styles.activeTab,
              { borderBottomColor: theme.colors.accent },
            ]}
            onPress={() => {
              setActiveTab(tab);
              setLoading(true);
            }}>
            <Text
              style={[
                styles.tabText,
                { color: activeTab === tab ? theme.colors.accent : theme.colors.textSecondary },
              ]}>
              {tab === 'active' ? 'In Progress' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {loading ? (
        <SkeletonRow rows={4} />
      ) : (
        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={async () => { setRefreshing(true); await loadDuels(); setRefreshing(false); }} tintColor={theme.colors.accent} />}>
          {/* What is a Duel? Explainer for new users */}
          {showDuelExplainer && (
            <View style={[styles.explainerCard, { backgroundColor: theme.colors.surface, borderLeftColor: '#B8972E' }]}>
              <View style={styles.explainerHeader}>
                <Text style={[styles.explainerTitle, { color: theme.colors.text }]}>WHAT IS A DUEL?</Text>
                <TouchableOpacity onPress={dismissDuelExplainer} style={styles.dismissButton}>
                  <X size={16} color={theme.colors.textSecondary} />
                </TouchableOpacity>
              </View>
              <Text style={[styles.explainerDescription, { color: theme.colors.text }]}>
                A DUEL is a head-to-head lifting challenge. Pick an exercise, challenge someone at your gym, and see who lifts more before time runs out.
              </Text>
            </View>
          )}

          {/* Duel Insights — Pro feature */}
          <DuelInsights />

          {/* Duel Stats (collapsible) */}
          <TouchableOpacity
            style={[styles.analyticsToggle, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}
            onPress={() => setShowAnalytics(!showAnalytics)}
            activeOpacity={0.7}
          >
            <Text style={[styles.analyticsToggleText, { color: theme.colors.text }]}>YOUR DUEL STATS</Text>
            {showAnalytics ? (
              <ChevronUp size={18} color={theme.colors.textSecondary} />
            ) : (
              <ChevronDown size={18} color={theme.colors.textSecondary} />
            )}
          </TouchableOpacity>
          {showAnalytics && (
            <View style={{ marginBottom: 12 }}>
              <DuelAnalytics />
            </View>
          )}

          {/* History filters for completed tab */}
          {activeTab === 'completed' && completedDuelsState.length > 0 && (
            <DuelHistoryFilters
              timeframeFilter={timeframeFilter}
              statusFilter={statusFilter}
              onTimeframeChange={setTimeframeFilter}
              onStatusChange={setStatusFilter}
            />
          )}

          {/* Recently completed banner */}
          {recentlyCompletedDuel && profile && (() => {
            const isWin = recentlyCompletedDuel.winner_user_id === profile.id;
            const isLoss = recentlyCompletedDuel.winner_user_id != null && recentlyCompletedDuel.winner_user_id !== profile.id;
            const isDraw = !recentlyCompletedDuel.winner_user_id;
            const otherId = recentlyCompletedDuel.challenger_id === profile.id
              ? recentlyCompletedDuel.challenged_id
              : recentlyCompletedDuel.challenger_id;
            const opp = otherId ? profileById[otherId] : null;
            const oppName = opp?.display_name ?? opp?.handle ?? 'Opponent';
            const margin = recentlyCompletedDuel.challenger_score != null && recentlyCompletedDuel.challenged_score != null
              ? `+${Math.abs(Math.round(recentlyCompletedDuel.challenger_score - recentlyCompletedDuel.challenged_score))} lbs`
              : undefined;
            return (
              <DuelResultBanner
                isWinner={isWin}
                isLoss={isLoss}
                isDraw={isDraw}
                opponentName={oppName}
                margin={margin}
              />
            );
          })()}

          {/* Incoming invites at top */}
          {incomingInvites.map((duel) => (
            <EnhancedDuelCard
              key={duel.id}
              duel={duel}
              currentUserId={profile!.id}
              profileById={profileById}
              onPress={(d) => { setSelectedDuel(d); setShowDetailModal(true); }}
              onAccept={acceptDuel}
              onDecline={rejectDuel}
              onCancel={cancelDuel}
            />
          ))}

          {(() => {
            const listToShow =
              activeTab === 'active'
                ? activeDuelsState
                : activeTab === 'pending'
                  ? outgoingPending
                  : filteredCompleted;

            if (listToShow.length === 0) {
              if (activeTab === 'completed') {
                return (
                  <EmptyState
                    icon={Swords}
                    title="No duels yet. Challenge someone at your gym to get started! 💪"
                    description="Season Zero is live at partner gyms."
                    actionLabel="Start a duel"
                    onAction={() => router.push('/(tabs)/duels?start_duel=1')}
                    secondaryActionLabel="View Leaderboards"
                    onSecondaryAction={() => router.push('/(tabs)/rank')}
                  />
                );
              }

              if (activeTab === 'active') {
                return (
                  <View style={{ padding: 24, alignItems: 'center', gap: 12 }}>
                    <Text style={{ color: theme.colors.text, fontSize: 18, fontWeight: '700' }}>
                      No active duels
                    </Text>
                    <Text style={{ color: theme.colors.textSecondary, fontSize: 14, textAlign: 'center' }}>
                      Challenge someone to prove your strength is legit.
                    </Text>
                    <TouchableOpacity
                      onPress={() => router.push('/(tabs)/duels?start_duel=1')}
                      style={{ backgroundColor: theme.colors.accent, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 4, marginTop: 8 }}>
                      <Text style={{ color: theme.colors.accentForeground, fontWeight: '800', fontSize: 15 }}>
                        Find an Opponent
                      </Text>
                    </TouchableOpacity>
                  </View>
                );
              }

              return (
                <EmptyState
                  icon={Swords}
                  title="No Pending Challenges"
                  description="You don't have any pending duel invitations. Create a challenge or wait for someone to challenge you!"
                  actionLabel="Find Opponents"
                  onAction={() => router.push('/(tabs)/duels?start_duel=1')}
                  secondaryActionLabel="View Leaderboards"
                  onSecondaryAction={() => router.push('/(tabs)/rank')}
                />
              );
            }

            return listToShow.map((duel) => (
              <EnhancedDuelCard
                key={duel.id}
                duel={duel}
                currentUserId={profile!.id}
                profileById={profileById}
                onPress={(d) => { setSelectedDuel(d); setShowDetailModal(true); }}
                onAccept={acceptDuel}
                onDecline={rejectDuel}
                onCancel={cancelDuel}
              />
            ));
          })()}
        </ScrollView>
      )}

      <CreateDuelWizard
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onDuelCreated={() => {
          setShowCreateModal(false);
          loadDuels();
        }}
      />

      <DuelDetailModal
        duel={selectedDuel}
        visible={showDetailModal}
        onClose={() => { setShowDetailModal(false); setSelectedDuel(null); loadDuels(); }}
        currentUserId={profile?.id ?? ''}
      />

      {showBaselineGate && baselineStatus && (
        <Modal visible animationType="slide" onRequestClose={() => setShowBaselineGate(false)}>
          <View style={{ flex: 1, position: 'relative' }}>
            <BaselineGateOverlay status={baselineStatus} feature="duels" />
            <TouchableOpacity
              style={{ position: 'absolute', top: 60, right: 20, zIndex: 10, padding: 8 }}
              onPress={() => setShowBaselineGate(false)}>
              <X size={24} color={theme.colors.text} />
            </TouchableOpacity>
          </View>
        </Modal>
      )}
      <InfoSheet
        visible={showDuelInfo}
        onClose={() => setShowDuelInfo(false)}
        title={INFO_CONTENT.duels.title}
        content={INFO_CONTENT.duels.content}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingTop: 12,
    paddingBottom: 20,
    paddingHorizontal: SPACE.lg,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: FONT['2xl'],
    fontFamily: TYPO.heading.bold,
    letterSpacing: TRACKING.wide,
    marginBottom: 12,
    textTransform: 'uppercase',
  },
  createButton: {
    ...BUTTON.primary,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  createButtonText: {
    fontSize: FONT.md,
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase',
  },
  tabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },
  tab: {
    flex: 1,
    paddingVertical: 16,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomWidth: 3,
  },
  tabText: {
    fontSize: FONT.base,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.wide,
  },
  content: {
    paddingHorizontal: SPACE.lg,
    paddingVertical: SPACE.md,
  },
  analyticsToggle: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: RADIUS.none,
    borderWidth: 1,
    marginBottom: 16,
  },
  analyticsToggleText: {
    ...SECTION_LABEL,
    fontFamily: TYPO.subheading.extrabold,
  },
  newcomerCard: {
    ...CARD.standard,
    borderWidth: 1,
    alignItems: 'center' as const,
    gap: 8,
    padding: 24,
  },
  newcomerCardTitle: {
    fontSize: FONT.lg,
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase' as const,
    letterSpacing: TRACKING.wide,
    textAlign: 'center' as const,
  },
  newcomerCardDesc: {
    fontSize: FONT.base,
    fontFamily: TYPO.body.regular,
    textAlign: 'center' as const,
    lineHeight: 22,
  },
  readyToCompete: {
    fontSize: FONT.lg,
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase' as const,
    letterSpacing: TRACKING.wide,
    marginBottom: 12,
  },
  explainerCard: {
    padding: 16,
    borderRadius: 2,
    borderLeftWidth: 4,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: 'rgba(184, 151, 46, 0.2)',
  },
  explainerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  explainerTitle: {
    fontSize: FONT.lg,
    fontFamily: TYPO.heading.bold,
    letterSpacing: TRACKING.wide,
    textTransform: 'uppercase',
  },
  dismissButton: {
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.7,
  },
  explainerDescription: {
    fontSize: FONT.base,
    fontFamily: TYPO.subheading.bold,
    lineHeight: 20,
  },
});
